import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Save, MapPin, CreditCard, Tag, Calendar, FileText, Loader2, Smartphone, Camera, X, ArrowDownCircle, ArrowUpCircle, Zap, RefreshCw, ArrowRight, Coins, Star, Image as ImageIcon, Check, ShoppingBag, Plus, Trash2 } from 'lucide-react';
import { ExpenseCategory, IncomeCategory, PaymentMethod, Transaction, TransactionType, PointProvider, ExpenseItem } from '../types';
import { parseExpenseText, verifyStoreNameWithGoogleMaps } from '../services/geminiService';
import { parseExpenseTextWithGroq } from '../services/groqService';
import { storageService } from '../services/storageService';

interface ExpenseFormProps {
  onViewChange: () => void;
  userId: string;
  initialData?: Transaction | null;
  onCheckGuest?: (callback: () => Promise<void>) => void;
}

interface ImageFile {
  id: string;
  file: File;
  previewUrl: string;
}

export const ExpenseForm: React.FC<ExpenseFormProps> = ({ onViewChange, userId, initialData, onCheckGuest }) => {
  const getNowString = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  // State
  const [type, setType] = useState<TransactionType>('expense');
  const [amount, setAmount] = useState<string>('');
  const [place, setPlace] = useState<string>('');
  
  // Methods
  const [method, setMethod] = useState<PaymentMethod>(PaymentMethod.CASH); 
  const [sourceMethod, setSourceMethod] = useState<PaymentMethod | undefined>(undefined); 
  
  // Point Specific
  const [pointProvider, setPointProvider] = useState<PointProvider>(PointProvider.PAYPAY);
  const [isPointUsage, setIsPointUsage] = useState<boolean>(false); 

  const [paymentDetail, setPaymentDetail] = useState<string>('');
  const [sourcePaymentDetail, setSourcePaymentDetail] = useState<string>('');
  const [category, setCategory] = useState<string>(ExpenseCategory.FOOD);
  const [date, setDate] = useState<string>(getNowString());
  const [note, setNote] = useState<string>('');
  
  // Items List
  const [items, setItems] = useState<ExpenseItem[]>([]);

  // Smart Input State
  const [smartInput, setSmartInput] = useState<string>('');
  const [selectedImages, setSelectedImages] = useState<ImageFile[]>([]);
  
  // UI State
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingModel, setProcessingModel] = useState<'gemini' | 'groq' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifyingPlace, setIsVerifyingPlace] = useState(false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null); 
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Computed state for parsing button
  const isSmartInputEmpty = smartInput.trim().length === 0;
  const hasImages = selectedImages.length > 0;
  const canParse = !isProcessing && (!isSmartInputEmpty || hasImages);

  // Load initial data
  useEffect(() => {
    if (initialData) {
        setType(initialData.type);
        setAmount(initialData.amount.toString());
        setPlace(initialData.place);
        setMethod(initialData.method);
        if (initialData.sourceMethod) setSourceMethod(initialData.sourceMethod);
        if (initialData.pointProvider) setPointProvider(initialData.pointProvider);
        if (initialData.isPointUsage !== undefined) setIsPointUsage(initialData.isPointUsage);
        if (initialData.paymentDetail) setPaymentDetail(initialData.paymentDetail);
        if (initialData.sourcePaymentDetail) setSourcePaymentDetail(initialData.sourcePaymentDetail);
        setCategory(initialData.category);
        setDate(initialData.date);
        if (initialData.note) setNote(initialData.note);
        if (initialData.items) setItems(initialData.items);
    } else {
        setType('expense');
        setAmount('');
        setPlace('');
        setMethod(PaymentMethod.CASH);
        setSourceMethod(undefined);
        setPointProvider(PointProvider.PAYPAY);
        setIsPointUsage(false);
        setPaymentDetail('');
        setSourcePaymentDetail('');
        setCategory(ExpenseCategory.FOOD);
        setDate(getNowString());
        setNote('');
        setItems([]);
        setSmartInput('');
        setSelectedImages([]);
    }
  }, [initialData]);

  useEffect(() => {
    if (initialData && initialData.type === type) return;

    if (type === 'expense') {
        setCategory(ExpenseCategory.FOOD);
        if (!initialData) setPlace('');
    } else if (type === 'income') {
        setCategory(IncomeCategory.SALARY);
        if (!initialData) setPlace('');
    } else if (type === 'charge') {
        setCategory(ExpenseCategory.CHARGE);
        setPlace('チャージ');
        if (!initialData) {
            setSourceMethod(undefined);
            setMethod(PaymentMethod.E_MONEY);
        }
    } else if (type === 'point') {
        setCategory('ポイント');
        setPlace('ポイント');
        setMethod(PaymentMethod.OTHER);
    }
  }, [type, initialData]);

  useEffect(() => {
    return () => {
      selectedImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
    };
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages: ImageFile[] = Array.from(e.target.files).map((file) => ({
        id: crypto.randomUUID(),
        file: file as File,
        previewUrl: URL.createObjectURL(file as File)
      }));
      setSelectedImages(prev => [...prev, ...newImages]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setSelectedImages(prev => {
      const target = prev.find(img => img.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(img => img.id !== id);
    });
  };

  // Item List Handlers
  const addItem = () => {
    setItems([...items, { name: '', amount: undefined }]);
  };

  const updateItem = (index: number, field: keyof ExpenseItem, value: string | number) => {
    const newItems = [...items];
    if (field === 'amount') {
         // handle empty string for amount
         if (value === '') newItems[index].amount = undefined;
         else newItems[index].amount = Number(value);
    } else {
        newItems[index].name = String(value);
    }
    setItems(newItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleSmartParse = async () => {
    if (!canParse) return;

    setIsProcessing(true);
    let data = null;

    try {
      const hasImages = selectedImages.length > 0;
      
      // Prepare image payloads first
      let imagePayloads: { data: string; mimeType: string }[] = [];
      if (hasImages) {
        imagePayloads = await Promise.all(
          selectedImages.map(async (img) => ({
            data: await convertFileToBase64(img.file),
            mimeType: img.file.type
          }))
        );
      }
      
      if (process.env.GROQ_API_KEY) {
        setProcessingModel('groq');
        data = await parseExpenseTextWithGroq(smartInput, imagePayloads);
      }

      if (!data) {
        setProcessingModel('gemini');
        data = await parseExpenseText(smartInput, imagePayloads);
      }
      
      if (data) {
        if (data.type) setType(data.type);
        if (data.amount) setAmount(data.amount.toString());
        
        // Place handling with verification
        let finalPlace = data.place || (data.type === 'charge' ? 'チャージ' : data.type === 'point' ? 'ポイント' : '');
        
        if (data.place && data.place !== 'ポイント' && data.place !== 'チャージ') {
            setIsVerifyingPlace(true);
            try {
                // Get current location if possible
                let location: { latitude: number; longitude: number } | undefined;
                if (navigator.geolocation) {
                    try {
                        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
                            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
                        });
                        location = {
                            latitude: pos.coords.latitude,
                            longitude: pos.coords.longitude
                        };
                    } catch (e: any) {
                        console.warn("Geolocation failed:", e.message || e);
                    }
                }

                const verifiedName = await verifyStoreNameWithGoogleMaps(data.place, location);
                if (verifiedName) {
                    finalPlace = verifiedName;
                }
            } catch (e) {
                console.error("Place verification failed", e);
            } finally {
                setIsVerifyingPlace(false);
            }
        }
        setPlace(finalPlace);
        
        if (data.date) {
            let dateTimeStr = data.date;
            if (data.time) {
                dateTimeStr = `${data.date}T${data.time}`;
            } else {
                const currentTime = new Date().toTimeString().slice(0, 5);
                dateTimeStr = `${data.date}T${currentTime}`;
            }
            if (dateTimeStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
                setDate(dateTimeStr);
            } else if (data.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                 const currentTime = new Date().toTimeString().slice(0, 5);
                 setDate(`${data.date}T${currentTime}`);
            }
        }
        if (data.note) setNote(data.note);
        if (data.paymentDetail) setPaymentDetail(data.paymentDetail);
        if (data.items) setItems(data.items);

        // Simple Mapping Logic inline for brevity in this update
        const mapMethod = (str: string | undefined): PaymentMethod | null => {
            if (!str) return null;
            const exactMatch = Object.values(PaymentMethod).find(m => m === str);
            if (exactMatch) return exactMatch;
            if (str.includes('Kyash') || str.includes('プリペイド') || str.includes('バンドル')) return PaymentMethod.PREPAID_CARD;
            if (str.includes('カード') || str.includes('クレカ')) return PaymentMethod.CREDIT_CARD;
            if (str.includes('Pay') || str.includes('QR') || str.includes('ペイ')) return PaymentMethod.QR_CODE;
            if (str.includes('Suica') || str.includes('IC') || str.includes('PASMO') || str.includes('WAON') || str.includes('nanaco')) return PaymentMethod.E_MONEY;
            if (str.includes('現金')) return PaymentMethod.CASH;
            if (str.includes('振込') || str.includes('銀行')) return PaymentMethod.BANK_TRANSFER;
            return null;
        };
        const detectedMethod = mapMethod(data.method);
        if (detectedMethod) {
            setMethod(detectedMethod);
            if (detectedMethod === PaymentMethod.PREPAID_CARD && (data.method?.includes('Kyash') || data.paymentDetail?.includes('Kyash'))) {
                setPaymentDetail('Kyash');
            }
        }

        if (data.type === 'charge' && data.sourceMethod) {
            const detectedSource = mapMethod(data.sourceMethod);
            if (detectedSource) setSourceMethod(detectedSource);
            if (data.sourcePaymentDetail) setSourcePaymentDetail(data.sourcePaymentDetail);
        }
        if (data.type === 'point') {
            const mapPointProvider = (str: string | undefined): PointProvider | null => {
                if (!str) return null;
                if (str.includes('PayPay')) return PointProvider.PAYPAY;
                if (str.includes('dポイント') || str.includes('ドコモ')) return PointProvider.DPOINT;
                if (str.includes('楽天')) return PointProvider.RAKUTEN;
                if (str.includes('Ponta')) return PointProvider.PONTA;
                if (str.includes('Vポイント') || str.includes('Tポイント')) return PointProvider.VPOINT;
                return PointProvider.OTHER;
            }
            const detectedProvider = mapPointProvider(data.pointProvider);
            if (detectedProvider) setPointProvider(detectedProvider);
            if (data.isPointUsage !== undefined) setIsPointUsage(data.isPointUsage);
        }
        
        if (data.category) {
            const allCategories = [...Object.values(ExpenseCategory), ...Object.values(IncomeCategory)];
            const exactMatch = allCategories.find(c => c === data.category);
            if (exactMatch) setCategory(exactMatch);
            else if (data.type === 'charge') setCategory(ExpenseCategory.CHARGE);
            else if (data.type === 'point') setCategory('ポイント');
        }
        setRequiresConfirmation(true);
      }
    } catch (e) {
      console.error("Failed to parse", e);
      alert("AI解析に失敗しました。");
    } finally {
      setIsProcessing(false);
      setProcessingModel(null);
    }
  };

  const executeSave = async () => {
    setIsSaving(true);
    try {
        const transactionData: Omit<Transaction, 'id' | 'createdAt'> = {
            type,
            amount: parseFloat(amount),
            place,
            method, 
            sourceMethod: type === 'charge' ? sourceMethod : undefined, 
            sourcePaymentDetail: type === 'charge' ? sourcePaymentDetail : undefined,
            pointProvider: (type === 'point' || method === PaymentMethod.POINTS) ? pointProvider : undefined,
            isPointUsage: type === 'point' ? isPointUsage : undefined,
            paymentDetail: (method === PaymentMethod.E_MONEY || method === PaymentMethod.QR_CODE || method === PaymentMethod.PREPAID_CARD) ? paymentDetail : undefined,
            category,
            items: items.length > 0 ? items.filter(i => i.name.trim() !== '') : undefined,
            date,
            note
        };

        if (initialData) {
            await storageService.updateTransaction(userId, {
                ...transactionData,
                id: initialData.id,
                createdAt: initialData.createdAt
            });
        } else {
            await storageService.addTransaction(userId, transactionData);
        }
        
        setAmount('');
        setPlace(type === 'charge' ? 'チャージ' : type === 'point' ? 'ポイント' : '');
        setSmartInput('');
        setNote('');
        setPaymentDetail('');
        setSourcePaymentDetail('');
        setSelectedImages([]);
        setItems([]);
        setDate(getNowString());
        
        onViewChange();
    } catch (e) {
        alert("保存に失敗しました。");
        console.error(e);
    } finally {
        setIsSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !place) return;
    if (type === 'charge' && sourceMethod && method === sourceMethod) {
        alert("チャージ元とチャージ先は別の方法を選択してください。");
        return;
    }

    if (requiresConfirmation) {
        setRequiresConfirmation(false);
        return;
    }

    if (userId === 'guest' && onCheckGuest) {
        onCheckGuest(executeSave);
        return;
    }
    
    await executeSave();
  };

  return (
    <div className="space-y-4 animate-fade-up relative">

      {/* --- AI ENTRY SECTION --- */}
      {!initialData && (
        <div className="card p-4 stagger-child animate-card" style={{border:'1.5px solid rgba(45,90,61,0.2)'}}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center animate-glow" style={{background:'var(--forest-pale)'}}>
                <Sparkles className="w-3.5 h-3.5 animate-pulse-soft" style={{color:'var(--forest)'}} />
              </div>
              <span className="font-semibold text-sm" style={{color:'var(--ink)'}}>AI入力アシスタント</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => fileInputRef.current?.click()}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-110 active:scale-90"
                style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}
                title="画像を選択">
                <ImageIcon className="w-4 h-4" />
              </button>
              <button onClick={() => cameraInputRef.current?.click()}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-110 active:scale-90"
                style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}
                title="カメラで撮影">
                <Camera className="w-4 h-4" />
              </button>
            </div>
          </div>

          {selectedImages.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-hide">
              {selectedImages.map((img, idx) => (
                <div key={img.id} className="relative shrink-0 w-16 h-16 rounded-xl overflow-hidden animate-scale-bounce" style={{animationDelay:`${idx*0.06}s`}}>
                  <img src={img.previewUrl} alt="Receipt" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(img.id)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 transition-transform hover:scale-110">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="relative">
            <textarea
              value={smartInput}
              onChange={e => setSmartInput(e.target.value)}
              placeholder="例: ファミマでおにぎり300円、PayPayで支払い"
              rows={2}
              className="input-warm w-full p-3.5 text-sm resize-none pr-12"
            />
            <button
              onClick={handleSmartParse}
              disabled={!canParse}
              className="absolute bottom-2.5 right-2.5 p-2 rounded-xl flex items-center justify-center"
              style={{
                background: canParse ? 'var(--forest)' : 'rgba(44,36,22,0.08)',
                color: canParse ? 'white' : 'var(--ink-muted)',
                cursor: canParse ? 'pointer' : 'not-allowed',
                boxShadow: canParse ? '0 2px 8px rgba(45,90,61,0.3)' : 'none',
                transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                transform: canParse ? 'scale(1)' : 'scale(0.9)',
              }}
              title="AIで解析">
              {isProcessing
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ArrowUpCircle className={`w-4 h-4 transition-transform duration-300 ${canParse ? 'hover:scale-110' : ''}`} />}
            </button>
          </div>

          <input type="file" ref={fileInputRef} accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
          <input type="file" ref={cameraInputRef} accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} />
        </div>
      )}

      {/* --- MANUAL FORM --- */}
      <form onSubmit={handleSubmit} className="space-y-4">
        
        {/* Type Switcher */}
        <div className="flex p-1 rounded-xl gap-0.5 stagger-child animate-fade-up" style={{background:'var(--cream-dark)'}}>
          {[
            { id: 'expense', label: '支出', icon: ArrowDownCircle, activeColor: 'var(--terra)' },
            { id: 'income', label: '収入', icon: ArrowUpCircle, activeColor: 'var(--forest)' },
            { id: 'charge', label: 'チャージ', icon: RefreshCw, activeColor: '#1a7a9e' },
            { id: 'point', label: 'P', icon: Coins, activeColor: '#8B6914' },
          ].map((t, i) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setType(t.id as TransactionType)}
              className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-lg text-xs font-bold ripple-container"
              style={{
                background: type === t.id ? 'white' : 'transparent',
                color: type === t.id ? t.activeColor : 'var(--ink-muted)',
                boxShadow: type === t.id ? '0 1px 3px rgba(44,36,22,0.1)' : 'none',
                transform: type === t.id ? 'scale(1.04)' : 'scale(1)',
                transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
              }}
            >
              <t.icon className={`w-3.5 h-3.5 transition-transform duration-300 ${type===t.id ? 'scale-110' : ''}`} />
              {t.label}
            </button>
          ))}
        </div>

        {initialData && (
          <div className="flex items-center justify-between px-1 animate-slide-down">
            <span className="tag tag-forest animate-bounce-in">編集中</span>
            <button type="button" onClick={onViewChange} className="text-xs underline transition-opacity hover:opacity-60" style={{color:'var(--ink-muted)'}}>キャンセル</button>
          </div>
        )}

        {/* --- MAIN INPUTS --- */}
        <div className="card p-5 space-y-4 stagger-child animate-fade-up delay-100">
            
            {/* Amount */}
            <div>
                <label className="text-[10px] font-bold uppercase tracking-widest ml-0.5 block mb-1.5 transition-all duration-300"
                  style={{color:'var(--ink-muted)'}}>
                  {type === 'point' ? 'ポイント数' : '合計金額'}
                </label>
                <div className="relative">
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0"
                        required
                        className="input-warm block w-full text-center py-4 text-3xl font-display font-600 tabular-nums transition-all duration-300"
                        style={{
                          color: amount && Number(amount) > 0
                            ? (type==='income' ? 'var(--forest)' : type==='expense' ? 'var(--terra)' : 'var(--ink)')
                            : 'var(--ink-muted)'
                        }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 font-semibold transition-colors duration-300" style={{color:'var(--ink-muted)'}}>
                        {type === 'point' ? 'P' : '円'}
                    </span>
                </div>
            </div>

            {/* Place */}
            {(type === 'expense' || type === 'income') && (
                <div className="animate-slide-down">
                     <label className="text-[10px] font-bold uppercase tracking-widest ml-0.5 block mb-1.5" style={{color:'var(--ink-muted)'}}>{type === 'expense' ? 'お店・場所' : '支払元・概要'}</label>
                     <div className="relative group">
                        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-200" style={{color:'var(--ink-muted)'}} />
                        <input
                            type="text"
                            value={place}
                            onChange={(e) => setPlace(e.target.value)}
                            required
                            placeholder="例: セブンイレブン"
                            className="input-warm block w-full pl-10 pr-4 py-3"
                        />
                     </div>
                </div>
            )}

            {/* --- SHOPPING ITEMS LIST --- */}
            {(type === 'expense' || type === 'income') && (
                <div className="border-t border-slate-100 pt-3 animate-fade-in">
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold text-slate-400 ml-1 flex items-center gap-1">
                            <ShoppingBag className="w-3 h-3" />
                            購入商品リスト
                        </label>
                        <button
                            type="button"
                            onClick={addItem}
                            className="text-[10px] font-bold text-indigo-500 bg-indigo-50 px-2 py-1 rounded-md flex items-center gap-1 hover:bg-indigo-100 transition-colors"
                        >
                            <Plus className="w-3 h-3" />
                            追加
                        </button>
                    </div>
                    
                    <div className="space-y-2">
                        {items.map((item, index) => (
                            <div key={index} className="flex gap-2 items-center animate-slide-in-down">
                                <input
                                    type="text"
                                    placeholder="商品名"
                                    value={item.name}
                                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                                    className="flex-1 py-2 px-3 bg-slate-50 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-indigo-50 outline-none transition-colors"
                                />
                                <div className="relative w-24">
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={item.amount === undefined ? '' : item.amount}
                                        onChange={(e) => updateItem(index, 'amount', e.target.value)}
                                        className="w-full py-2 px-3 pr-6 bg-slate-50 rounded-lg text-sm text-right focus:bg-white focus:ring-2 focus:ring-indigo-50 outline-none transition-colors tabular-nums"
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">円</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeItem(index)}
                                    className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {items.length === 0 && (
                            <div className="text-center py-2 text-xs text-slate-300 bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
                                商品リストは空です
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Point specific toggle */}
            {type === 'point' && (
                <div className="grid grid-cols-2 gap-3 animate-fade-in">
                    <button
                        type="button"
                        onClick={() => setIsPointUsage(false)}
                        className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${
                            !isPointUsage ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-100 bg-slate-50 text-slate-400'
                        }`}
                    >
                        貯める (Get)
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsPointUsage(true)}
                        className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${
                            isPointUsage ? 'border-slate-700 bg-slate-800 text-white' : 'border-slate-100 bg-slate-50 text-slate-400'
                        }`}
                    >
                        使う (Use)
                    </button>
                </div>
            )}
            
            {/* Charge Logic UI */}
            {type === 'charge' && (
                <div className="bg-cyan-50/50 rounded-2xl p-4 border border-cyan-100 space-y-3 animate-zoom-in">
                     <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <label className="text-[10px] font-bold text-cyan-700 block mb-1">元 (From)</label>
                            <select
                                value={sourceMethod || ''}
                                onChange={(e) => setSourceMethod(e.target.value ? e.target.value as PaymentMethod : undefined as any)}
                                className="w-full p-2 bg-white rounded-lg border border-cyan-200 text-sm font-medium"
                            >
                                <option value="">選択しない</option>
                                {Object.values(PaymentMethod).map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <select
                                value={sourcePaymentDetail || ''}
                                onChange={(e) => setSourcePaymentDetail(e.target.value)}
                                className="w-full mt-2 p-2 bg-white rounded-lg border border-cyan-200 text-xs text-cyan-800 font-medium focus:outline-none"
                                disabled={!sourceMethod}
                            >
                                <option value="">詳細を選択...</option>
                                {sourceMethod === PaymentMethod.BANK_TRANSFER && (
                                    <>
                                        <option value="三菱UFJ銀行">三菱UFJ銀行</option>
                                        <option value="三井住友銀行">三井住友銀行</option>
                                        <option value="みずほ銀行">みずほ銀行</option>
                                        <option value="ゆうちょ銀行">ゆうちょ銀行</option>
                                        <option value="楽天銀行">楽天銀行</option>
                                        <option value="住信SBIネット銀行">住信SBIネット銀行</option>
                                        <option value="PayPay銀行">PayPay銀行</option>
                                        <option value="ソニー銀行">ソニー銀行</option>
                                        <option value="その他">その他</option>
                                    </>
                                )}
                                {sourceMethod === PaymentMethod.CREDIT_CARD && (
                                    <>
                                        <option value="楽天カード">楽天カード</option>
                                        <option value="三井住友カード">三井住友カード</option>
                                        <option value="dカード">dカード</option>
                                        <option value="au PAY カード">au PAY カード</option>
                                        <option value="PayPayカード">PayPayカード</option>
                                        <option value="イオンカード">イオンカード</option>
                                        <option value="エポスカード">エポスカード</option>
                                        <option value="JCBカード">JCBカード</option>
                                        <option value="AMEX">AMEX</option>
                                        <option value="その他">その他</option>
                                    </>
                                )}
                                {sourceMethod === PaymentMethod.E_MONEY && (
                                    <>
                                        <option value="Suica">Suica</option>
                                        <option value="PASMO">PASMO</option>
                                        <option value="ICOCA">ICOCA</option>
                                        <option value="WAON">WAON</option>
                                        <option value="nanaco">nanaco</option>
                                        <option value="QUICPay">QUICPay</option>
                                        <option value="iD">iD</option>
                                        <option value="楽天Edy">楽天Edy</option>
                                        <option value="その他">その他</option>
                                    </>
                                )}
                                {sourceMethod === PaymentMethod.QR_CODE && (
                                    <>
                                        <option value="PayPay">PayPay</option>
                                        <option value="d払い">d払い</option>
                                        <option value="楽天ペイ">楽天ペイ</option>
                                        <option value="au PAY">au PAY</option>
                                        <option value="メルペイ">メルペイ</option>
                                        <option value="LINE Pay">LINE Pay</option>
                                        <option value="その他">その他</option>
                                    </>
                                )}
                                {sourceMethod === PaymentMethod.PREPAID_CARD && (
                                    <>
                                        <option value="Kyash">Kyash</option>
                                        <option value="au PAY プリペイド">au PAY プリペイド</option>
                                        <option value="dカード プリペイド">dカード プリペイド</option>
                                        <option value="バンドルカード">バンドルカード</option>
                                        <option value="ソフトバンクカード">ソフトバンクカード</option>
                                        <option value="その他">その他</option>
                                    </>
                                )}
                                {sourceMethod === PaymentMethod.DEBIT_CARD && (
                                    <>
                                        <option value="三菱UFJデビット">三菱UFJデビット</option>
                                        <option value="三井住友デビット">三井住友デビット</option>
                                        <option value="みずほデビット">みずほデビット</option>
                                        <option value="楽天銀行デビット">楽天銀行デビット</option>
                                        <option value="住信SBIデビット">住信SBIデビット</option>
                                        <option value="Sony Bank WALLET">Sony Bank WALLET</option>
                                        <option value="PayPay銀行デビット">PayPay銀行デビット</option>
                                        <option value="その他">その他</option>
                                    </>
                                )}
                                {sourceMethod === PaymentMethod.CASH && (
                                    <>
                                        <option value="財布">財布</option>
                                        <option value="小銭入れ">小銭入れ</option>
                                        <option value="貯金箱">貯金箱</option>
                                        <option value="その他">その他</option>
                                    </>
                                )}
                                {sourceMethod === PaymentMethod.OTHER && (
                                    <option value="その他">その他</option>
                                )}
                            </select>
                        </div>
                        <ArrowRight className="w-4 h-4 text-cyan-400 mt-4" />
                        <div className="flex-1">
                            <label className="text-[10px] font-bold text-cyan-700 block mb-1">先 (To)</label>
                            <select
                                value={method}
                                onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                                className="w-full p-2 bg-white rounded-lg border border-cyan-200 text-sm font-medium"
                            >
                                {Object.values(PaymentMethod).map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                     </div>
                </div>
            )}

            {/* Methods & Category Grid */}
            {(type === 'expense' || type === 'income') && (
                <div className="grid grid-cols-2 gap-3 animate-fade-in">
                    <div>
                        <label className="text-xs font-bold text-slate-400 ml-1 block mb-1.5">カテゴリ</label>
                        <div className="relative">
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="block w-full pl-3 pr-8 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-indigo-300 focus:outline-none text-sm font-medium text-slate-700 appearance-none"
                            >
                                {type === 'expense' ? (
                                    Object.values(ExpenseCategory).map((c) => <option key={c} value={c}>{c}</option>)
                                ) : (
                                    Object.values(IncomeCategory).map((c) => <option key={c} value={c}>{c}</option>)
                                )}
                            </select>
                            <Tag className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-400 ml-1 block mb-1.5">{type === 'expense' ? '支払方法' : '受取方法'}</label>
                        <div className="relative">
                            <select
                                value={method}
                                onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                                className="block w-full pl-3 pr-8 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-indigo-300 focus:outline-none text-sm font-medium text-slate-700 appearance-none"
                            >
                                {Object.values(PaymentMethod).map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <CreditCard className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                    </div>
                </div>
            )}

            {/* Point Provider */}
            {(type === 'point' || (type === 'expense' && method === PaymentMethod.POINTS)) && (
                <div className="animate-fade-in">
                    <label className="text-xs font-bold text-slate-400 ml-1 block mb-1.5">ポイント種類</label>
                    <div className="relative">
                        <select
                            value={pointProvider}
                            onChange={(e) => setPointProvider(e.target.value as PointProvider)}
                            className="block w-full pl-3 pr-8 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-indigo-300 focus:outline-none text-sm font-medium text-slate-700 appearance-none"
                        >
                            {Object.values(PointProvider).map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <Coins className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400 pointer-events-none" />
                    </div>
                </div>
            )}

             {/* Detail Select for Payment Method */}
             {(type === 'charge' || (type !== 'point' && method !== PaymentMethod.POINTS)) && (
                 <div className="animate-slide-in-down">
                    <label className="text-xs font-bold text-slate-400 ml-1 block mb-1.5">詳細 (任意)</label>
                    <select
                        value={paymentDetail}
                        onChange={(e) => setPaymentDetail(e.target.value)}
                        className="block w-full px-3 py-2.5 bg-indigo-50/50 border border-indigo-100 rounded-xl focus:outline-none text-sm text-indigo-800 font-medium"
                    >
                        <option value="">詳細を選択...</option>
                        {method === PaymentMethod.BANK_TRANSFER && (
                            <>
                                <option value="三菱UFJ銀行">三菱UFJ銀行</option>
                                <option value="三井住友銀行">三井住友銀行</option>
                                <option value="みずほ銀行">みずほ銀行</option>
                                <option value="ゆうちょ銀行">ゆうちょ銀行</option>
                                <option value="楽天銀行">楽天銀行</option>
                                <option value="住信SBIネット銀行">住信SBIネット銀行</option>
                                <option value="PayPay銀行">PayPay銀行</option>
                                <option value="ソニー銀行">ソニー銀行</option>
                                <option value="その他">その他</option>
                            </>
                        )}
                        {method === PaymentMethod.CREDIT_CARD && (
                            <>
                                <option value="楽天カード">楽天カード</option>
                                <option value="三井住友カード">三井住友カード</option>
                                <option value="dカード">dカード</option>
                                <option value="au PAY カード">au PAY カード</option>
                                <option value="PayPayカード">PayPayカード</option>
                                <option value="イオンカード">イオンカード</option>
                                <option value="エポスカード">エポスカード</option>
                                <option value="JCBカード">JCBカード</option>
                                <option value="AMEX">AMEX</option>
                                <option value="その他">その他</option>
                            </>
                        )}
                        {method === PaymentMethod.E_MONEY && (
                            <>
                                <option value="Suica">Suica</option>
                                <option value="PASMO">PASMO</option>
                                <option value="ICOCA">ICOCA</option>
                                <option value="WAON">WAON</option>
                                <option value="nanaco">nanaco</option>
                                <option value="QUICPay">QUICPay</option>
                                <option value="iD">iD</option>
                                <option value="楽天Edy">楽天Edy</option>
                                <option value="その他">その他</option>
                            </>
                        )}
                        {method === PaymentMethod.QR_CODE && (
                            <>
                                <option value="PayPay">PayPay</option>
                                <option value="d払い">d払い</option>
                                <option value="楽天ペイ">楽天ペイ</option>
                                <option value="au PAY">au PAY</option>
                                <option value="メルペイ">メルペイ</option>
                                <option value="LINE Pay">LINE Pay</option>
                                <option value="その他">その他</option>
                            </>
                        )}
                        {method === PaymentMethod.PREPAID_CARD && (
                            <>
                                <option value="Kyash">Kyash</option>
                                <option value="au PAY プリペイド">au PAY プリペイド</option>
                                <option value="dカード プリペイド">dカード プリペイド</option>
                                <option value="バンドルカード">バンドルカード</option>
                                <option value="ソフトバンクカード">ソフトバンクカード</option>
                                <option value="その他">その他</option>
                            </>
                        )}
                        {method === PaymentMethod.DEBIT_CARD && (
                            <>
                                <option value="三菱UFJデビット">三菱UFJデビット</option>
                                <option value="三井住友デビット">三井住友デビット</option>
                                <option value="みずほデビット">みずほデビット</option>
                                <option value="楽天銀行デビット">楽天銀行デビット</option>
                                <option value="住信SBIデビット">住信SBIデビット</option>
                                <option value="Sony Bank WALLET">Sony Bank WALLET</option>
                                <option value="PayPay銀行デビット">PayPay銀行デビット</option>
                                <option value="その他">その他</option>
                            </>
                        )}
                        {method === PaymentMethod.CASH && (
                            <>
                                <option value="財布">財布</option>
                                <option value="小銭入れ">小銭入れ</option>
                                <option value="貯金箱">貯金箱</option>
                                <option value="その他">その他</option>
                            </>
                        )}
                        {method === PaymentMethod.OTHER && (
                            <option value="その他">その他</option>
                        )}
                    </select>
                 </div>
             )}

            {/* Date & Note */}
            <div className="grid grid-cols-1 gap-4 pt-2">
                 <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Calendar className="w-5 h-5 text-slate-400" />
                    <input
                        type="datetime-local"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                        className="bg-transparent text-sm font-medium text-slate-600 focus:outline-none w-full"
                    />
                 </div>
                 <div className="flex items-start gap-2 border-b border-slate-100 pb-2">
                    <FileText className="w-5 h-5 text-slate-400 mt-0.5" />
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={1}
                        placeholder="メモを残す..."
                        className="bg-transparent text-sm font-medium text-slate-600 focus:outline-none w-full resize-none placeholder:text-slate-300"
                    />
                 </div>
            </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSaving}
          className="w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-2 ripple-container stagger-child animate-fade-up delay-200"
          style={{
            background: requiresConfirmation ? 'var(--gold)' :
              type === 'expense' ? 'var(--terra)' :
              type === 'income' ? 'var(--forest)' :
              type === 'charge' ? '#1a7a9e' : 'var(--gold)',
            boxShadow: requiresConfirmation ? '0 4px 16px rgba(201,168,76,0.3)' :
              type === 'expense' ? '0 4px 16px rgba(194,103,58,0.3)' :
              type === 'income' ? '0 4px 16px rgba(45,90,61,0.3)' : '0 4px 16px rgba(26,122,158,0.3)',
            transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; }}
          onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px) scale(0.98)'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)'; }}
        >
          {isSaving ? <Loader2 className="animate-spin w-5 h-5" /> : requiresConfirmation ? <Check className="w-5 h-5" /> : <Save className="w-5 h-5" />}
          {requiresConfirmation ? '内容を確認して記録' : (initialData ? '更新する' : '記録する')}
        </button>
      </form>
    </div>
  );
};